import React, { useState } from 'react';
import type { SimpleInterpolation } from 'styled-components';
import styled from 'styled-components';
import { isNil } from 'lodash';
import { Form, Formik } from 'formik';
import type { SocialNetworkNames } from 'src/components/AuthNew/utils/socialNetworkUtils';
import { AuthSuccess } from 'src/components/AuthNew/components/AuthSuccess';
import { useTranslation } from '@lt/localization';

import { useSelector } from 'react-redux';
import { getIsHotelCheckout } from 'src/store/view/selectors';
import { FormFields } from './types';
import type { FormValues } from './types';
import {
  LoginFormTypeForAnalytics,
  RequestStatus,
  LoginFormType,
} from '../../types';
import { Title } from '../Title';
import { Description } from '../Description';
import { Content } from '../Content';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import { SocialAuth } from '../SocialAuth';
import type { LoginConfig, SharedState, SetSharedState } from '../../types';

import { resetPhoneFormat, resetPersonDataFormat } from '../../utils';
import { EmailInput, PhoneInput, SwitchFormButtons } from './components';
import { getEmailError } from './helpers';
import {
  emailValidationSchema,
  phoneValidationSchema,
} from './validationSchema';
import { pushAuthContinue } from '../../analytics';

interface Props {
  isWhiteLabel?: boolean;
  onSubmit: (config: LoginConfig) => Promise<void>;
  onSocialNetworkSubmit: (socialNetwork: SocialNetworkNames) => void;
  requestStatus: RequestStatus;
  setSharedState: SetSharedState;
  sharedState: SharedState;
  loginFormCSS?: ReadonlyArray<SimpleInterpolation>;
}

export const LoginForm: React.FC<Props> = ({
  isWhiteLabel,
  onSubmit,
  onSocialNetworkSubmit,
  requestStatus,
  setSharedState,
  sharedState,
  loginFormCSS,
}) => {
  const isHotelCheckout = useSelector(getIsHotelCheckout);
  const initialValues: FormValues = {
    /**
     * Переименовал из-за того, что эти значения поставляются в name в инпут,
     * из-за этого в поле для номера телефона предлагает вставить почту
     */
    phone: sharedState?.phone || '',
    email: sharedState?.email || '',
  };
  const [loginFormType, setLoginFormType] = useState(
    initialValues.email ? LoginFormType.Email : LoginFormType.Phone,
  );
  const { t } = useTranslation();

  const handleSubmit = async ({ phone, email }: FormValues) => {
    const login = loginFormType === LoginFormType.Email ? email : phone;
    if (isNil(login)) {
      return;
    }

    const isEmail = isNil(getEmailError(login));

    if (isEmail) {
      pushAuthContinue(LoginFormTypeForAnalytics.Email);
      await onSubmit({ email: resetPersonDataFormat(login), type: 'email' });
    } else {
      pushAuthContinue(LoginFormTypeForAnalytics.Phone);
      await onSubmit({ phone: resetPhoneFormat(login), type: 'phone' });
    }
  };

  const loginFormDescriptionContext = `${isWhiteLabel ? '' : 'cashback'}${
    !isWhiteLabel && isHotelCheckout ? '_' : ''
  }${isHotelCheckout ? 'hotel_checkout' : ''}`;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={
        loginFormType === LoginFormType.Email
          ? emailValidationSchema
          : phoneValidationSchema
      }
    >
      {({ isSubmitting }) => {
        const disableSubmitButton =
          isSubmitting || requestStatus === RequestStatus.Submitting;

        return (
          <Form>
            <StyledTitle>{t('checkout.loginForm.title')}</StyledTitle>
            <Description>
              {t('checkout.loginForm.description', {
                context: loginFormDescriptionContext,
              })}
            </Description>
            <SwitchFormButtons
              loginFormType={loginFormType}
              setLoginFormType={setLoginFormType}
              loginFormCSS={loginFormCSS}
            />
            <StyledContent>
              <Wrapper $formType={loginFormType} $loginFormCSS={loginFormCSS}>
                {loginFormType === LoginFormType.Phone && (
                  <PhoneInput
                    setSharedState={setSharedState}
                    sharedState={sharedState}
                    name={FormFields.LoginByPhone}
                  />
                )}
                {loginFormType === LoginFormType.Email && (
                  <EmailInput name={FormFields.LoginByEmail} />
                )}
                <ButtonWithLoader
                  size="large"
                  primary
                  type="submit"
                  isLoading={disableSubmitButton}
                  disabled={disableSubmitButton}
                  width="100%"
                >
                  {t('checkout.loginForm.continue')}
                </ButtonWithLoader>
              </Wrapper>
              {requestStatus === RequestStatus.Succeeded && <AuthSuccess />}
              {requestStatus !== RequestStatus.Succeeded && !isWhiteLabel && (
                <SocialAuth
                  disabled={disableSubmitButton}
                  loginSocialNetwork={onSocialNetworkSubmit}
                />
              )}
            </StyledContent>
          </Form>
        );
      }}
    </Formik>
  );
};

const StyledTitle = styled(Title)`
  white-space: pre-line;
`;

const StyledContent = styled(Content)`
  margin-top: 0 !important;
`;

const Wrapper = styled.div<{
  $formType: LoginFormType;
  $loginFormCSS?: ReadonlyArray<SimpleInterpolation>;
}>`
  ${(props) =>
    props.$loginFormCSS ? props.$loginFormCSS : 'background: #f5f3f2'};
  border-radius: ${(props) =>
    props.$formType === LoginFormType.Phone ? '0 4px 4px 4px' : '4px'};
  padding: 12px;
`;
